import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import { defaultActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { ActionStatus } from 'models/WorkflowAction';
import {
  archiveFacet,
  createFacet,
  WorkflowActionIntent,
  workflowQueryKeys,
  WorkflowValuesInput,
} from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import ClockIcon from 'svg/ClockIcon';
import DeactivatedUserIcon from 'svg/DeactivatedUserIcon';
import ErrorOccurredIcon from 'svg/ErrorOccurredIcon';
import NoteIcon from 'svg/NoteIcon';
import NoteIconOutline from 'svg/NoteIconOutline';
import SSOErrorIcon from 'svg/SSOErrorIcon';

import { conjoin, labelForValue } from '../actionConfig';
import NotesTab, { NotesCopy } from '../clinicalReview/notes/NotesTab';
import { VALUE_NAMES } from '../constants';
import DetailSlideout from '../DetailSlideout';

import AddCoverageModal from './AddCoverageModal';
import CoverageHistory from './CoverageHistory';
import {
  COVERAGE_TAB_LABELS,
  coverageForTab,
  coveragesOf,
  coverageTabLabel,
  coverageTabs,
  HISTORY_TAB,
  NOTES_TAB,
  tabForFacet,
} from './coverageTabs';
import {
  BENEFITS_ELIGIBILITY_KIND,
  blockedValueNames,
  currentVerification,
  EXTERNAL_PAYER_ID_VALUE,
  PAYER_LOGO_VALUE,
  readEligibility,
} from './eligibilityData';
import EligibilityDetailsHeader from './EligibilityDetailsHeader';
import EligibilityEmptyState from './EligibilityEmptyState';
import EligibilityFooter from './EligibilityFooter';
import EligibilityResultView from './EligibilityResultView';
import ManualBenefitsForm, { MANUAL_BENEFITS_FORM_ID, ManualBenefitsSubmission } from './ManualBenefitsForm';
import ManualBenefitsView from './ManualBenefitsView';
import RerunConfirmModal from './RerunConfirmModal';
import SelectPayerFooter from './SelectPayerFooter';
import SelectPayerView from './SelectPayerView';

// The Insurance Eligibility Check slideout. Opened from the Payer Search / Insurance Verification
// rows; derives which of the six states to show from the two auto-check actions and renders the
// matching subview. Interactions run through the same complete/invoke handlers the card uses:
// picking a payer completes PayerSearch with its external_payer_id; Try Again re-runs BenefitsEligibility.

const NOTES_COPY: NotesCopy = {
  heading: 'Benefit Verification Notes',
  subcopy:
    'Capture payer and coverage context — who you spoke to, why a result was overridden, or what is still pending.',
  placeholder: 'Start typing here to add a note for additional context for the team',
};

// The step's note thread is ONE thread across every coverage and every re-run: the API reads any
// verification's thread as the union of all of them (WorkflowAction#note_thread_actions), so which
// instance a note is posted against is a storage detail. Posting goes through the current
// verification, and the unread flag the API sets on each instance is the same answer, so the row
// and tab read any of them.
function noteThread(workflow: Workflow): { anchor: WorkflowAction | undefined; actions: WorkflowAction[] } {
  return {
    anchor: currentVerification(workflow),
    actions: workflow.actions.filter((action) => action.kind === BENEFITS_ELIGIBILITY_KIND),
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
  onEditDetails?: () => void;
  // The coverage being verified (design 008 §3e); null on a referral with no coverages. Every
  // value this slideout renders belongs to one coverage, so an unscoped read would show a
  // sibling coverage's payer next to this one's results.
  facetId: string | null;
  // Documents this coverage's benefits by hand. Resolves once saved; rejects (already toasted) when refused.
  onDocumentBenefits?: (actionId: string, submission: ManualBenefitsSubmission) => Promise<unknown>;
  isDocumentingBenefits?: boolean;
  /** The tab a deep link names, applied once per arrival; the row's coverage otherwise. */
  initialTab?: string | null;
  /** What tells one arrival from the next — the link's note id — so a second notification on the
   * same referral lands on its tab again. */
  arrivalId?: string | null;
  /** The note a mention link pointed at, to be paged forward to and scrolled into view. */
  focusActivityId?: string | null;
  /** Called once the note has been reached, so the arrival is not replayed on a later mount. */
  onFocusHandled?: () => void;
}

// The value every auto-check waits on; benefit verification hasn't started without it.
const DUPLICATE_PATIENT_RESULT = 'duplicate_patient_result';

export default function EligibilityCheckSlideout({
  open,
  onClose,
  workflow,
  onComplete,
  isCompleting,
  onInvoke,
  isInvoking,
  onEditDetails,
  facetId,
  onDocumentBenefits,
  isDocumentingBenefits = false,
  initialTab,
  arrivalId,
  focusActivityId,
  onFocusHandled,
}: Props) {
  const { anchor: threadAction, actions: threadActions } = useMemo(() => noteThread(workflow), [workflow]);
  const unreadNotes = threadActions.some((action) => action.hasNewActivities);
  const tabs = useMemo<TabType[]>(() => {
    const coverage = coverageTabs(workflow);
    // Only where there is a verification to hang the thread on, and only beside the coverage strip:
    // a referral with no coverages has no strip at all, and a lone Notes tab over an unlabeled body
    // would name one view and not the other.
    if (coverage.length === 0 || !threadAction) return coverage;

    return [
      ...coverage,
      {
        label: 'Notes',
        value: NOTES_TAB,
        'data-cy': 'coverage-tab-notes',
        // The same mark and the same flag as the row icon — one flag, two surfaces.
        affix: unreadNotes ? (
          <NoteIcon color={colors.blue100} width={12} height={13} data-testid='notes-tab-unread' />
        ) : undefined,
      },
    ];
  }, [workflow, threadAction, unreadNotes]);
  // Only beside a coverage strip (see `tabs`): a referral with none keeps the single verification
  // view, so a link naming Notes lands on that view rather than on a tab it cannot show.
  const notesOffered = tabs.some((option) => option.value === NOTES_TAB);
  const [tab, setTab] = useState(() => (initialTab && notesOffered ? initialTab : tabForFacet(workflow, facetId)));
  // Which row opened the slideout, so the tab only re-syncs when a DIFFERENT row opens it. Keyed on
  // the row rather than on the workflow: the referral reloads every few seconds while this is open, and
  // syncing on that would throw the reader off whichever coverage they were reading, mid-read.
  const openedFor = useRef(facetId);
  const [addingCoverage, setAddingCoverage] = useState(false);
  // The payer picked but not yet run. Lives here rather than in SelectPayerView because the action
  // that uses it sits in the slideout's pinned footer — and because it must reset when the shown
  // coverage changes, or a pick made on one coverage would arm the button on another.
  const [selectedPayerId, setSelectedPayerId] = useState<string | null>(null);
  const [confirmingRerun, setConfirmingRerun] = useState(false);
  const [documenting, setDocumenting] = useState(false);
  // The coverage "+ Add Benefits Manually" filed into an empty slot, and its verification. Kept so a
  // retry after a refused save documents into that coverage instead of filing a second one, and so the
  // form stays open while the referral reloads onto it.
  const filed = useRef<{ facetId: string; benefitsId?: string } | null>(null);
  const [canSaveManual, setCanSaveManual] = useState(false);
  const onCanSaveChange = useCallback((canSave: boolean) => setCanSaveManual(canSave), []);
  // The row that opened the slideout decides which coverage it opens on, so clicking the secondary's
  // flagged verification lands on Secondary rather than on Primary with the reader to work out why.
  useEffect(() => {
    if (openedFor.current === facetId) return;

    openedFor.current = facetId;
    setTab(tabForFacet(workflow, facetId));
  }, [workflow, facetId]);

  // Arriving by deep link: land on the tab the link names. Once per arrival rather than while the
  // params remain, or the referral's periodic reload would drag a reader who moved to Primary back
  // to Notes. Keyed on the link's note rather than on the focus, which the page clears once the
  // note is reached — re-firing then would drag a reader back too. Runs after the row sync above,
  // so the arrival wins when both fire on one open.
  const arrival = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !initialTab || !notesOffered) return;
    const key = `${facetId}:${initialTab}:${arrivalId ?? ''}`;
    if (arrival.current === key) return;

    arrival.current = key;
    openedFor.current = facetId;
    setTab(initialTab);
  }, [open, initialTab, notesOffered, facetId, arrivalId]);

  // A rank can lose its slot while somebody is reading it: an untouched seeded coverage is archived
  // once extraction finds a real one (OLIO-3167), and the referral reloads under the reader. Falling
  // back keeps the strip from highlighting nothing while the body invites them to add the very
  // coverage they were reading.
  const activeTab = tab === HISTORY_TAB || tabs.some((option) => option.value === tab) ? tab : (tabs[0]?.value ?? tab);
  const selectedTab = tabs.find((option) => option.value === activeTab);
  const onHistory = activeTab === HISTORY_TAB;
  const onNotes = activeTab === NOTES_TAB && notesOffered && !!threadAction;
  // The notes composer's draft lives out here rather than in NotesTab: the slideout swaps tabs by
  // conditional render, so a trip to Primary and back would otherwise drop a half-typed note.
  const [noteDraft, setNoteDraft] = useState(defaultActivityInputValues);
  // Owned here only because NotesTab's contract asks for it. The verification fans no note out — its
  // thread already spans every coverage — so the box is never offered (groupCount 1) and this never
  // moves off false.
  const [sendToAll, setSendToAll] = useState(false);
  // Which coverage the body is showing. On a referral with no coverages this stays the facetId the
  // caller passed (null) — that referral has one run-level verification and no tabs at all.
  const coverage = tabs.length === 0 ? null : coverageForTab(workflow, activeTab);
  const shownFacetId = tabs.length === 0 ? facetId : (coverage?.id ?? null);

  // A payer picked on one coverage must not arm the run button on another — the footer's action
  // completes THAT coverage's payer search.
  useEffect(() => {
    // Except the coverage this form just filed: the tab moving from the empty slot onto it is the save
    // in progress, not the reader switching coverages.
    if (shownFacetId && shownFacetId === filed.current?.facetId) return;

    setSelectedPayerId(null);
    setDocumenting(false);
  }, [shownFacetId]);

  // A tab whose coverage doesn't exist yet — the Secondary slot on a referral that only has a primary.
  // Distinct from a referral with NO coverages, which has no tabs and one run-level verification.
  const emptySlot = tabs.length > 0 && !coverage;
  // Which slot is empty decides the words. It is not always the secondary: a packet naming only a
  // secondary leaves the PRIMARY slot empty, and this used to invite a secondary either way
  // (OLIO-3141).
  const emptySlotLabel = coverageTabLabel(Number(activeTab));

  const { payerSearch, benefits, candidates, result, manual, state } = readEligibility(workflow, shownFacetId);
  // Every write this slideout offers, answered in one place. Each rides the grant the API's policy
  // checks for it, so a reader is never shown a control that can only end in a 403 — and a new
  // condition on who may act is added here rather than at each control.
  const { canEdit, canEditAction } = useWorkflowPermissions();
  // Picking a payer completes PayerSearch, so the pick and the button that submits it both ride
  // that action's own grant. A view-only account keeps the candidate list — which payers the search
  // returned is the step's result, and reading a referral's results is the point of the account —
  // but the cards stop responding and the footer's Run button comes off rather than sitting there
  // to be clicked into a 403.
  const canSelectPayer = payerSearch ? canEditAction(payerSearch, workflow) : false;
  // Re-running is the verification's own write, so it rides that action's grant — the footer's
  // Re-Run and the failure states' Try Again are the same `rerun` and come off together. What they
  // sit beside stays: when the check last ran, its PDF, and the copy naming what went wrong are all
  // readings of the result.
  const canRerunCheck = benefits ? canEditAction(benefits, workflow) : false;
  // Filing a coverage has no action behind it — `createFacet` writes the referral — so it rides
  // what the header's edit menu asks for (InboundReferralPage): workflow-level edit, on a referral
  // that is not locked.
  const canAddCoverage = canEdit && !workflow.isLocked;
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const { mutateAsync: fileCoverage, isPending: isFilingCoverage } = useMutateWithToast({ mutationFn: createFacet });
  const { mutateAsync: removeCoverage } = useMutateWithToast({ mutationFn: archiveFacet });
  const applySaved = useCallback(
    (saved: Workflow) => {
      queryClient.setQueryData(workflowQueryKeys.show(saved.id), saved);
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.listPrefix });
    },
    [queryClient]
  );
  // A coverage this form filed whose manual save never landed (refused, then abandoned) would stay behind
  // as a blank insurance with nothing on it. Leaving the form removes it, so the slot reads empty again.
  // Archived rather than deleted, as every coverage removal is. A failed removal is toasted and leaves the
  // coverage, which still works as an ordinary empty one.
  const discardFiled = useCallback(() => {
    const coverage = filed.current;
    filed.current = null;
    if (!coverage) return;

    removeCoverage({ workflowId: workflow.id, facetId: coverage.facetId })
      .then(applySaved)
      .catch(() => undefined);
  }, [applySaved, removeCoverage, workflow.id]);
  useEffect(() => {
    if (open) return;
    discardFiled();
    setDocumenting(false);
  }, [open, discardFiled]);
  // The API refuses the same cases: a check or payer search in flight, a step closed with the decision
  // or not applicable, and a referral that hasn't reached benefit verification yet.
  const benefitsReachable =
    Boolean(onDocumentBenefits) &&
    !workflow.isLocked &&
    !workflow.isTerminal &&
    !workflow.hasOutcome &&
    workflow.valueFor(DUPLICATE_PATIENT_RESULT)?.value != null;
  const canDocumentBenefits =
    benefitsReachable &&
    !!benefits?.facetId &&
    canRerunCheck &&
    ![ActionStatus.RUNNING, ActionStatus.CANCELED, ActionStatus.SKIPPED].includes(benefits.status) &&
    payerSearch?.status !== ActionStatus.RUNNING;
  // An empty slot has no verification yet: saving files the coverage with no details first, so its
  // payer search has nothing to search and never starts, then documents into it. Filing a coverage
  // rides referral-level Edit, like "+ Add Secondary Insurance".
  const canDocumentIntoEmptySlot = benefitsReachable && emptySlot && canAddCoverage;
  const savingManual = isDocumentingBenefits || isFilingCoverage;
  // Only the entry point is gated: a refresh that starts a check under an open form must not throw away
  // what was typed. The API refuses a save the moment it can't apply, and says why.
  const showingForm = documenting && (!!benefits || emptySlot);
  const showingManual = !showingForm && state === 'manual' && !!manual;
  // What the payer search searched for: this COVERAGE's payer name. `plan_type` is only the right
  // answer for a referral that has no coverages and whose verification is still run-level — one
  // created before OLIO-2890 stopped referral intake capturing a payer of its own.
  const payerName = shownFacetId
    ? workflow.valueFor(VALUE_NAMES.PAYER_NAME, { facetId: shownFacetId })?.value
    : workflow.valueFor(VALUE_NAMES.PLAN_TYPE)?.value;
  const memberId = workflow.valueFor(VALUE_NAMES.MEMBER_ID, { facetId: shownFacetId })?.value;
  // Committed at pick time (PayerSearch's candidates are gone by result time — see readEligibility).
  const payerLogo = (workflow.valueFor(PAYER_LOGO_VALUE, { facetId: shownFacetId })?.value as string) || undefined;
  // Until a check has run there is nothing to report and nothing to act on — no "last ran", no PDF,
  // and nothing to re-run. Suppress the whole bar rather than render an empty one: while the payer is
  // still being picked, SelectPayerView owns the primary action, and a second (dead) button beneath it
  // read as broken.
  const hasFooterContent = Boolean(benefits?.completedAt) || Boolean(result?.pdf?.eligibilityCheckId);
  const selectingPayer = state === 'select-payer';
  // What a parked check is waiting on, named by the API on the flagged action itself.
  const blockedValues = blockedValueNames({ payerSearch, benefits });

  const runCheck = (payerId: string) => {
    if (!payerSearch) return;
    // Carry the selected payer's logo forward so the result Overview can show it.
    const logo = candidates.find((c) => c.primaryPayerId === payerId)?.logo ?? '';
    onComplete(payerSearch.id, { [EXTERNAL_PAYER_ID_VALUE]: payerId, [PAYER_LOGO_VALUE]: logo });
  };
  const retry = () => {
    if (benefits) onInvoke(benefits.id, 'rerun');
  };
  const confirmRerun = () => {
    setConfirmingRerun(false);
    retry();
  };
  // The verification to document into: this coverage's, or — on an empty slot — the one on the
  // coverage filed for it, filing that coverage first if this is the first try.
  const benefitsToDocument = async () => {
    if (benefits) return benefits.id;
    if (!emptySlot) return undefined;
    if (filed.current?.benefitsId) return filed.current.benefitsId;

    const saved: Workflow = await fileCoverage({
      workflowId: workflow.id,
      dimension: 'coverage',
      position: Number(activeTab),
      // The claim on the slot, as "+ Add Secondary Insurance" sends it (AddCoverageModal).
      ...(COVERAGE_TAB_LABELS[Number(activeTab)] ? { label: COVERAGE_TAB_LABELS[Number(activeTab)] } : {}),
      values: {},
    });
    const known = new Set(coveragesOf(workflow).map((cov) => cov.id));
    const added = coveragesOf(saved).find((cov) => !known.has(cov.id));
    const addedBenefits = added ? readEligibility(saved, added.id).benefits : undefined;
    if (added) filed.current = { facetId: added.id, benefitsId: addedBenefits?.id };
    applySaved(saved);
    // The coverage is filed but its verification isn't in the answer yet. Saying so beats a Save that
    // does nothing; the next try finds the verification on the refreshed referral and files nothing new.
    if (!addedBenefits) addToast({ text: 'The insurance was added. Save again to record its benefits.' });
    return addedBenefits?.id;
  };
  const saveManual = async (submission: ManualBenefitsSubmission) => {
    if (!onDocumentBenefits) return;
    // Rejected filings are already toasted; the form stays open for another try.
    const actionId = await benefitsToDocument().catch(() => undefined);
    if (!actionId) return;

    onDocumentBenefits(actionId, submission)
      .then(() => {
        filed.current = null;
        setDocumenting(false);
      })
      .catch((error) => {
        // The step changed under the form — someone else saved, or a check started. Show the referral as
        // it now stands rather than let a retry replace what they did, and leave the coverage: it is
        // somebody's now.
        if ((error as { response?: { status?: number } })?.response?.status === 409) {
          filed.current = null;
          setDocumenting(false);
        }
      });
  };

  return (
    <>
      {/* The row icon renders with the slideout shut, so these subscriptions outlive it. One per
          verification instance, because every channel is keyed to a single record id; a note on
          any of them is a note on the one thread. */}
      {threadActions.map((action) => (
        <ThreadSubscription
          key={action.id}
          workflowId={workflow.id}
          actionId={action.id}
          threadActionIds={threadActions.map((sibling) => sibling.id)}
          notesOpen={open && onNotes}
        />
      ))}
      <DetailSlideout
        open={open}
        onClose={onClose}
        title={`${workflow.patientName}: Benefit Verification`}
        subtitle='Verifying patient has active insurance coverage and eligibility'
        tabs={
          tabs.length > 0 ? (
            <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(t) => setTab(t.value)} />
          ) : undefined
        }
        hideHeaderBorder
        detailsBar={
          onHistory || onNotes || showingForm || showingManual ? undefined : emptySlot ? (
            // The same row a filled coverage shows, so "+ Add Benefits Manually" is offered before any
            // automatic check exists. Edit has nothing to edit yet, so it adds the coverage instead.
            <EligibilityDetailsHeader
              workflow={workflow}
              facetId={null}
              emptySlot
              onEditDetails={canAddCoverage ? () => setAddingCoverage(true) : undefined}
              onAddManually={canDocumentIntoEmptySlot ? () => setDocumenting(true) : undefined}
            />
          ) : (
            <EligibilityDetailsHeader
              workflow={workflow}
              onEditDetails={onEditDetails}
              facetId={shownFacetId}
              coverageStatus={
                state === 'active-coverage' ? 'active' : state === 'no-active-coverage' ? 'inactive' : undefined
              }
              onAddManually={canDocumentBenefits ? () => setDocumenting(true) : undefined}
            />
          )
        }
        footer={
          onHistory || onNotes || showingManual ? undefined : showingForm ? (
            <FormActions>
              <Button
                variant='ghost'
                onClick={() => {
                  discardFiled();
                  setDocumenting(false);
                }}
                disabled={savingManual}
                data-testid='manual-benefits-cancel'>
                Cancel
              </Button>
              <Button
                type='submit'
                form={MANUAL_BENEFITS_FORM_ID}
                disabled={!canSaveManual || savingManual}
                loading={savingManual}
                data-testid='manual-benefits-save'>
                Save Benefit Information
              </Button>
            </FormActions>
          ) : emptySlot ? undefined : selectingPayer ? (
            !canSelectPayer ? undefined : (
              <SelectPayerFooter
                selectedId={selectedPayerId}
                onRun={() => selectedPayerId && runCheck(selectedPayerId)}
                isRunning={isCompleting}
              />
            )
          ) : !hasFooterContent ? undefined : (
            <EligibilityFooter
              workflowId={workflow.id}
              benefits={benefits}
              facetId={shownFacetId}
              pdfAvailable={Boolean(result?.pdf?.eligibilityCheckId)}
              onRerun={canRerunCheck ? () => setConfirmingRerun(true) : undefined}
              isRerunning={isInvoking}
            />
          )
        }>
        {onNotes ? (
          <NotesBody>
            <NotesTab
              workflowId={workflow.id}
              closedToNotes={workflow.isLocked || workflow.isArchived}
              action={threadAction}
              threadActionIds={threadActions.map((action) => action.id)}
              groupCount={1}
              copy={NOTES_COPY}
              values={noteDraft}
              setValues={setNoteDraft}
              sendToAll={sendToAll}
              setSendToAll={setSendToAll}
              focusActivityId={focusActivityId}
              onFocusHandled={onFocusHandled}
            />
          </NotesBody>
        ) : onHistory ? (
          <CoverageHistory workflow={workflow} />
        ) : showingForm ? (
          <ManualBenefitsForm
            record={state === 'manual' ? manual : null}
            payerName={payerName ? String(payerName) : undefined}
            memberId={memberId ? String(memberId) : undefined}
            disabled={savingManual}
            onSubmit={saveManual}
            onCanSaveChange={onCanSaveChange}
          />
        ) : showingManual && manual && benefits ? (
          <ManualBenefitsView
            workflowId={workflow.id}
            record={manual}
            benefits={benefits}
            onEdit={canDocumentBenefits ? () => setDocumenting(true) : undefined}
          />
        ) : emptySlot ? (
          /* A coverage slot nobody has filled: an invitation, not a failure — so no status, no result,
           and no footer actions, because there is no check to act on yet. */
          <EligibilityEmptyState
            tone='neutral'
            icon={<NoteIconOutline width={40} height={45} color={colors.black75} />}
            title={`No ${emptySlotLabel} Insurance Added Yet`}
            body={
              // The invitation is the acting half of this state, so it goes with the button: a reader
              // told to "add the details below" with nothing below to add them in has been sent
              // somewhere that doesn't exist. What stays is the fact the slot is empty, which is the
              // reason they opened the tab.
              canAddCoverage ? (
                <>
                  If this patient has a {emptySlotLabel.toLowerCase()} insurance plan,
                  <br />
                  add the details below to run a benefit verification
                </>
              ) : (
                `This referral has no ${emptySlotLabel.toLowerCase()} insurance on file.`
              )
            }
            action={
              canAddCoverage ? (
                <Button
                  onClick={() => setAddingCoverage(true)}
                  data-testid={`add-${emptySlotLabel.toLowerCase()}-insurance`}>
                  + Add {emptySlotLabel} Insurance
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {selectingPayer && (
              <SelectPayerView
                candidates={candidates}
                searchTerm={payerName}
                selectedId={selectedPayerId}
                onSelect={setSelectedPayerId}
                canSelect={canSelectPayer}
              />
            )}

            {state === 'active-coverage' && result && (
              <EligibilityResultView result={result} active payerLogo={payerLogo} />
            )}
            {state === 'no-active-coverage' && result && (
              <EligibilityResultView result={result} active={false} payerLogo={payerLogo} />
            )}

            {state === 'payer-not-enrolled' && (
              <EligibilityEmptyState
                icon={<SSOErrorIcon width={56} height={56} />}
                title='Payer Not Enrolled in Eligibility Check'
                body={
                  <>
                    Unfortunately, this patient’s payer is not enrolled in Olio’s automated eligibility check.
                    <br />
                    Reach out to the payer for details around this patient’s eligibility.
                  </>
                }
              />
            )}

            {state === 'no-results' && (
              <EligibilityEmptyState
                icon={<DeactivatedUserIcon width={56} height={56} />}
                title='No Results Found'
                body={
                  <>
                    This patient couldn’t be found with the provided information.
                    <br />
                    <Emphasis>
                      Please verify the member ID, name, and date of birth match the insurance card, then try again.
                    </Emphasis>
                  </>
                }
                onTryAgain={canRerunCheck ? retry : undefined}
                isRetrying={isInvoking}
              />
            )}

            {/* The search came back with nothing to pick (OLIO-3426, QA). No retry offered: re-running the
              same query returns the same nothing, and what has to change is the payer name on the
              referral. Kept apart from 'no-results' because that one IS worth retrying — there the
              payer was found and the eligibility check missed the patient.
              Neutral, like `blocked` and unlike the three states above it: the search did its job and
              reported honestly, and a person fixing an input is an invitation rather than a failure.
              The opener is carried as a BUTTON gated on onEditDetails rather than named in the copy,
              for the reason `blocked` does the same — a viewer with no edit grant has no Edit Details
              link on the page, and telling them to use one sends them somewhere that isn't there
              (OLIO-2615/3141). Without the grant the sentence still stands on its own. */}
            {state === 'no-payer-match' && (
              <EligibilityEmptyState
                tone='neutral'
                icon={<DeactivatedUserIcon width={56} height={56} />}
                title='No Matching Payer Found'
                body={
                  <>
                    No payer matched the insurance details provided for this patient.
                    <br />
                    <Emphasis>Please check the payer name against the patient’s insurance card.</Emphasis>
                  </>
                }
                action={
                  onEditDetails ? (
                    <Button onClick={onEditDetails} data-testid='eligibility-edit-payer'>
                      Edit Details
                    </Button>
                  ) : undefined
                }
              />
            )}

            {state === 'error' && (
              <EligibilityEmptyState
                icon={<ErrorOccurredIcon width={56} height={56} />}
                title='Something Went Wrong'
                body={
                  <>
                    Something went wrong while checking eligibility.
                    <br />
                    Please try again or contact support if the issue persists.
                  </>
                }
                onTryAgain={canRerunCheck ? retry : undefined}
                isRetrying={isInvoking}
              />
            )}

            {/* Reachable only if the slideout was open when the run got canceled (poll flips the
          state under it) — the row-level entry links are suppressed for canceled. */}
            {state === 'canceled' && (
              <EligibilityEmptyState
                icon={<DeactivatedUserIcon width={56} height={56} />}
                title='Eligibility Check Canceled'
                body='This referral was canceled before the eligibility check finished.'
              />
            )}

            {state === 'running' && (
              <EligibilityEmptyState
                icon={<CircularProgress color={colors.blue100} size={32} />}
                title='Running eligibility check…'
                body='This can take a few moments. The results will appear here when the check completes.'
              />
            )}

            {/* The check is parked on information the engine can't derive (OLIO-3330). Names the
              missing fields from the action's own `unobtainableValues` rather than a list kept
              here — the API decides what a step requires, and a hardcoded copy would drift the
              first time the contract changed. Same substance as the checklist's FlaggedPrompt,
              which this slideout never reached: the eligibility row renders as its own component,
              so it bypassed the flagged copy every other step gets.

              No opener means no edit permission (OLIO-2615) — the copy still explains what is
              missing, but the link that would fix it isn't offered. */}
            {state === 'blocked' && (
              <EligibilityEmptyState
                icon={<NoteIconOutline width={40} height={45} color={colors.black75} />}
                tone='neutral'
                title='More information is needed to run a benefit verification'
                body={
                  // The name list can be empty — the flag is what puts us here, not the names (see
                  // deriveEligibilityState). Degrade to the same sentence the checklist falls back to
                  // rather than rendering "This check can't run until  is added to the referral."
                  blockedValues.length === 0 ? (
                    'This check needs more information before it can run.'
                  ) : (
                    <>
                      This check can’t run until{' '}
                      <BlockedValues data-testid='eligibility-blocked-values'>
                        {conjoin(blockedValues.map((name) => labelForValue(workflow, name)))}
                      </BlockedValues>{' '}
                      {blockedValues.length > 1 ? 'are' : 'is'} added to the referral.
                    </>
                  )
                }
                action={
                  onEditDetails ? (
                    <Button onClick={onEditDetails} data-testid='eligibility-add-details'>
                      Add Details
                    </Button>
                  ) : undefined
                }
              />
            )}

            {/* A clock, not the spinner: nothing is running yet, and a spinner over "hasn't run"
              read as work in progress. Matches how the checklist draws a step that hasn't
              started — never animated. */}
            {state === 'pending' && (
              <EligibilityEmptyState
                icon={<ClockIcon width={56} height={56} />}
                title='Eligibility check not started'
                body='This check hasn’t run yet. It will start once the required details are available.'
              />
            )}
          </>
        )}

        {addingCoverage && (
          <AddCoverageModal
            workflowId={workflow.id}
            onClose={() => setAddingCoverage(false)}
            label={emptySlotLabel}
            position={Number(activeTab)}
            // Only for a slot the vocabulary names. Past Secondary there is no sequence to claim, so
            // nothing is claimed and the API appends.
            claim={COVERAGE_TAB_LABELS[Number(activeTab)]}
            // Land on the coverage just added rather than back on the primary — the person filled that
            // form to watch this check start.
            //
            // Land on the coverage just added rather than back on the primary — the person filled
            // that form to watch this check start.
            //
            // Its own rank, not the end of the list: a coverage filed into the empty PRIMARY slot
            // leaves a list whose LAST entry is the pre-existing secondary, so counting the list sent
            // the person to the wrong coverage (OLIO-3141). Falls back to the slot just filled when
            // the saved payload has not caught up.
            onAdded={(saved) =>
              setTab(
                String(coveragesOf(saved).find((cov) => cov.position === Number(activeTab))?.position ?? activeTab)
              )
            }
          />
        )}

        {confirmingRerun && (
          <RerunConfirmModal
            payerName={String(payerName ?? '')}
            memberId={String(memberId ?? '')}
            onConfirm={confirmRerun}
            onCancel={() => setConfirmingRerun(false)}
            isRerunning={isInvoking}
          />
        )}
      </DetailSlideout>
    </>
  );
}

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

/**
 * Live updates for one verification instance's thread while the Notes tab is NOT on screen: lights
 * the unread flag on every instance of the step, since they report as one. NotesTab carries its own
 * subscription for the open thread; useWebsocketSubscription ref-counts by identifier, so when both
 * are live they share one ActionCable subscription and each handler runs.
 */
function ThreadSubscription({
  workflowId,
  actionId,
  threadActionIds,
  notesOpen,
}: {
  workflowId: string;
  actionId: string;
  threadActionIds: string[];
  notesOpen: boolean;
}) {
  const { profile } = useProfile();
  const queryClient = useQueryClient();

  useWebsocketSubscription<{ id: string; activities?: boolean; actorId: string | null }>({
    channel: 'WorkflowActionChannel',
    params: { workflow_action_id: actionId },
    onReceived: (data) => {
      // Your own notes never count as unread.
      if (data.actorId === profile?.id) return;
      // The subscribe-time transmit is { id, actorId } with no association key.
      if (!data.activities) return;
      // The note is already on screen, and NotesTab's invalidation refetches page 1 — which stamps
      // the arriving note read. Flipping the flag here would light an icon that is wrong the
      // instant it appears.
      if (notesOpen) return;

      queryClient.setQueryData<Workflow>(
        workflowQueryKeys.show(workflowId),
        (prev) => prev?.withUnreadActivitiesOn(threadActionIds) ?? prev
      );
    },
  });

  return null;
}

// NotesTab keeps its composer fixed and scrolls only the thread, which needs a bounded flex column
// to shrink inside. DetailSlideout's body pins every direct child to its natural height so the body
// itself scrolls; this child opts back out, at the body's own specificity.
const NotesBody = styled.div`
  && {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }
`;

// A bare <strong> takes the browser's 700, which is heavier than this brand's bold (Special
// Gothic is variable; FONT_WEIGHT_MEDIUM/500 is the theme's mapping) — still a <strong> element,
// the emphasis is real, it just takes the theme's weight instead of the browser default.
const Emphasis = styled.strong`
  font-weight: ${FONT_WEIGHT_MEDIUM};
`;

// The missing field names, emphasized inside the sentence rather than listed beneath it — the
// reader's question is "what is it waiting on", and the answer belongs in the copy they are
// already reading. Same weight treatment as Emphasis above, with a test hook so the names can be
// asserted without matching the whole sentence.
const BlockedValues = styled.span`
  color: ${colors.black100};
  font-weight: ${FONT_WEIGHT_MEDIUM};
`;
